import { useState, useEffect } from "react";
import { MotifProgressLoader, MotifIcon } from "@ey-xd/motif-react";
import { IconoirWarningCircle } from "@ey-xd/motif-icon";
import orderEmptyImg from "../../../assets/images/empty_order.svg";
import OrdersTable from "../OrderTable/OrderTable";
import "./MyEstimates.scss";
import { getEstimatedOrderColumns } from "../tableColumn";
import EmptyOrder from "../../EmptyOrder";
import NoSearchResult from "../../NoSearchResult/NoSearchResult";
import productListEmptyImg from "../../../assets/images/empty_product_list_default.svg";
import ErrorOrderPage from "../../ErrorOrderPage/ErrorOrderPage";
import { getRetiredSkuListInWorkSpace } from "../helper";
import { RETIRED_SKU_IN_CHECKOUT_MESSAGE } from "../../../Constant";
import { containsEmoji } from "../../../utils/common";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

interface MyEstimatesProps {
  estimatedOrders: any[];
  isLoadingEstimates: boolean;
  getOrderAction?: (order: any) => React.ReactNode;
  searchText?: string;
  isEstimateError: boolean;
  refreshCallback: () => void;
  handleSyncOrder?: (orderId: number, status?: string) => void;
}

const MyEstimates: React.FC<MyEstimatesProps> = ({
  estimatedOrders,
  isLoadingEstimates,
  getOrderAction,
  searchText,
  isEstimateError,
  refreshCallback,
  handleSyncOrder,
}) => {
  const [estimatedOrderDetails, setEstimatedOrderDetails] = useState<
    {
      orderParentId: string;
      isExpanded: boolean;
    }[]
  >([]);

  const emailId: string = useSelector(
    (state: RootState) => state.UserProfileSlice.emailId,
  );

  useEffect(() => {
    if (estimatedOrders.length > 0) {
      const newOrderDetails = estimatedOrders.map((order: any) => ({
        orderParentId: order.OrderParentId,
        isExpanded: false,
      }));
      setEstimatedOrderDetails(newOrderDetails);
    }
  }, [estimatedOrders]);

  if (isLoadingEstimates) {
    return <MotifProgressLoader show variant="default" />;
  }

  const estimatedOrderColumns = getEstimatedOrderColumns(
    setEstimatedOrderDetails,
    estimatedOrderDetails,
    getOrderAction,
    emailId,
    handleSyncOrder,
  );

  const retiredSkuList = getRetiredSkuListInWorkSpace(estimatedOrders);

  const renderRetiredErrorMessage = () => {
    if (retiredSkuList.length === 0) {
      return;
    }
    return (
      <div
        className={`sku-retired-error-message ${
          retiredSkuList.length === 0 ? "hide" : ""
        }`}
      >
        <MotifIcon src={IconoirWarningCircle} className="warning-icon" />
        <div className="error-message">{RETIRED_SKU_IN_CHECKOUT_MESSAGE}</div>
      </div>
    );
  };

  let content;

  if (!estimatedOrders.length) {
    if (isEstimateError) {
      content = (
        <ErrorOrderPage
          image={productListEmptyImg}
          header="An error has occurred while attempting to retrieve data. Please choose the option to refresh the page."
          handleRefresh={refreshCallback}
        />
      );
    } else {
      const isContainEmoji = containsEmoji(searchText || "");
      if (searchText && !isContainEmoji) {
        content = <NoSearchResult searchValue={searchText} />;
      } else {
        content = (
          <EmptyOrder
            image={orderEmptyImg}
            header="No estimates created yet"
            subHeader="It looks like you do not have any estimates created yet. Please browse our technology products to start creating estimates."
          />
        );
      }
    }
  } else {
    content = (
      <OrdersTable
        data={estimatedOrders}
        orderDetails={estimatedOrderDetails}
        columnsData={estimatedOrderColumns}
        showEngagementColumn={true}
        retiredSkuList={retiredSkuList}
        showRetiredError={true}
      />
    );
  }

  return (
    <div className="workspace-orders-section">
      <div className="workspace-orders-list">
        <div className="workspace-orders-header">
          <div className="workspace-orders-header-grid">
            <div>
              <h3 className="workspace-orders-title">My Estimates</h3>
              <p className="workspace-orders-description">Estimates Created</p>
            </div>
            <div>{renderRetiredErrorMessage()}</div>
          </div>
        </div>
        <div className="workspace-orders-content">{content}</div>
      </div>
    </div>
  );
};

export default MyEstimates;
